import { FC, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { MailIcon, PlusSmIcon } from '@heroicons/react/outline';
import { ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import {
  FollowUpTemplateDataFragment,
  useAutomationRulesQuery,
  useDeleteFollowUpTemplateMutation,
  useDuplicateFollowUpTemplateMutation,
  useFollowUpTemplatesQuery,
} from '../../../../generated/graphql';

import Button from '../../../components/Button';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import IllustrationMail from '../../../svgs/IllustrationMail';
import Modal from '../../../components/Modal';
import ResourceInUseModal, {
  AutomationReference,
} from '../../../components/Modals/ResourceInUseModal';
import { customToast } from '../../../components/ToastAlert/customToast';
import { defaultTransitionProps } from '../../../lib/animation';
import {
  isFollowUpTemplateUsedInAutomation,
  parseAutomationReferencesFromError,
} from '../../../lib/automationHelpers';
import { isProviderUserOwnerOrAdminAuthorized } from '../../../../lib/auth';
import { useAuth } from '../../../../contexts/AuthContext';
import Loading from '../../Loading';
import FollowUpTemplateCard from '../../FollowUpBuilder/FollowUpDrawer/FollowUpTemplateCard';

const MailTemplates: FC = () => {
  const navigate = useNavigate();
  const { authedProviderUser } = useAuth();
  const canManageAutomations =
    isProviderUserOwnerOrAdminAuthorized(authedProviderUser);

  const [templateToDelete, setTemplateToDelete] =
    useState<FollowUpTemplateDataFragment | null>(null);
  const [templateInUseInfo, setTemplateInUseInfo] = useState<{
    templateName?: string | null;
    automationReferences: AutomationReference[];
  } | null>(null);

  const { data, loading, error, refetch } = useFollowUpTemplatesQuery({
    variables: { input: { isDraft: false } },
    fetchPolicy: 'cache-and-network',
    onError: (err) => {
      customToast.error(err.message || 'Could not load Mail templates.');
    },
  });

  const { data: automationRulesData } = useAutomationRulesQuery({
    fetchPolicy: 'cache-first',
  });
  const automationRules = automationRulesData?.automationRules ?? [];

  const [duplicateTemplate] = useDuplicateFollowUpTemplateMutation({
    onCompleted: () => {
      customToast.success('Template duplicated.');
      refetch();
    },
    onError: () => customToast.error('Failed to duplicate template.'),
  });

  const [deleteTemplate, { loading: deleting }] =
    useDeleteFollowUpTemplateMutation({
      onCompleted: () => {
        customToast.success('Template deleted.');
        setTemplateToDelete(null);
        refetch();
      },
      onError: (err) => {
        if (err instanceof ApolloError && err.graphQLErrors?.[0]) {
          const automationReferences = parseAutomationReferencesFromError(
            err.graphQLErrors[0].message || '',
          );
          if (automationReferences && templateToDelete) {
            setTemplateInUseInfo({
              templateName: templateToDelete.name,
              automationReferences,
            });
            setTemplateToDelete(null);
            return;
          }
        }
        customToast.error('Failed to delete template.');
      },
    });

  const templates = useMemo(
    () =>
      [...(data?.followUpTemplates ?? [])].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [data?.followUpTemplates],
  );

  const openEdit = (template: FollowUpTemplateDataFragment) => {
    navigate(`/automations/templates/mail/${template.id}/edit`);
  };

  if (loading && !data) return <Loading />;

  return (
    <div className="flex w-full flex-col pb-8">
      <div className="mb-6 flex items-center justify-between gap-4 px-1">
        <div>
          <h1 className="mb-1 font-serif text-subtitle text-neutral-150">
            Mail templates
          </h1>
        </div>
        <Button
          title="New Mail template"
          IconComponent={PlusSmIcon}
          size="small"
          iconPosition="right"
          theme="primary"
          onClick={() => navigate('/automations/templates/mail/new')}
        />
      </div>

      <Transition show={!loading && !error} {...defaultTransitionProps}>
        {templates.length === 0 ? (
          <EmptyProfileTab
            IconComponent={IllustrationMail}
            iconClassName="text-secondary-100"
            titleText="No Mail templates yet"
            subTitleText="Create a Mail template to send to client with ease."
            onClickButton={() => navigate('/automations/templates/mail/new')}
            buttonTitle="New Mail template"
            ButtonIconComponent={MailIcon}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 px-3 py-2 sm:grid-cols-2 xl:grid-cols-3">
            {templates.map((template) => (
              <FollowUpTemplateCard
                key={template.id}
                followUpTemplate={template}
                renameActionLabel="Edit template"
                isUsedInAutomation={isFollowUpTemplateUsedInAutomation(
                  template.id,
                  automationRules,
                )}
                onApplyFollowUpTemplate={async (followUpTemplate) => {
                  openEdit(followUpTemplate);
                }}
                onRenameFollowUpTemplate={async (followUpTemplate) => {
                  openEdit(followUpTemplate);
                }}
                onDuplicateFollowUpTemplate={async (followUpTemplate) => {
                  await duplicateTemplate({
                    variables: { input: { id: followUpTemplate.id } },
                  });
                }}
                onDeleteFollowUpTemplate={async (followUpTemplate) => {
                  setTemplateToDelete(followUpTemplate);
                }}
              />
            ))}
          </div>
        )}
      </Transition>

      <Modal
        isOpen={Boolean(templateToDelete)}
        setClosed={() => setTemplateToDelete(null)}
        width="small"
      >
        <div className="px-10 py-8">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex text-center font-serif text-subtitle-small text-green-150">
              Delete Mail template?
            </div>
            <div className="text-center text-body text-green-125">
              {templateToDelete
                ? `“${
                    templateToDelete.name?.trim() || 'Untitled template'
                  }” will be removed. Automations that use it must be updated first.`
                : ''}
            </div>
          </div>
          <div className="mt-11 flex w-full flex-row items-center justify-center">
            <Button
              title="Cancel"
              theme="secondary"
              className="mr-5"
              onClick={() => setTemplateToDelete(null)}
              disabled={deleting}
            />
            <Button
              title={deleting ? 'Deleting...' : 'Delete'}
              theme="primary"
              disabled={deleting}
              onClick={() => {
                if (!templateToDelete) return;
                deleteTemplate({
                  variables: { input: { id: templateToDelete.id } },
                });
              }}
            />
          </div>
        </div>
      </Modal>

      <ResourceInUseModal
        isOpen={Boolean(templateInUseInfo)}
        setClosed={() => setTemplateInUseInfo(null)}
        resourceType="email_template"
        resourceName={templateInUseInfo?.templateName ?? 'This template'}
        automationReferences={templateInUseInfo?.automationReferences ?? []}
        canViewAutomations={canManageAutomations}
      />
    </div>
  );
};

export default MailTemplates;
